import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Send, Plus, Trash2, Stethoscope, Target, Activity, HeartPulse,
  CalendarClock, ClipboardCheck, Siren, Utensils, Users2, FileText, History, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { SectionCard, Field, ChipMultiSelect, toOpts } from "@/components/careplan/CarePlanFields";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EDIT_ROLES = ["hah_physician", "physician", "admin"];

function Picker({ value, onChange, options, placeholder, disabled, testId }) {
  return (
    <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? null : v)} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— {placeholder || "Select"} —</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function CarePlanEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.includes(user?.role_code);

  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [content, setContent] = useState(null);
  const [careTeam, setCareTeam] = useState({ physician_id: null, physician_ids: [], nurse_ids: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [newCrit, setNewCrit] = useState("");
  const [newInterv, setNewInterv] = useState("");
  const [formOpts, setFormOpts] = useState([]);

  useEffect(() => {
    api.get("/forms", { params: { status: "published" } })
      .then((r) => setFormOpts((r.data.items || []).map((f) => ({ value: f.id, label: f.name }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([api.get(`/care-plans/${id}`), api.get(`/care-plans/meta`)]);
      setPlan(p.data); setMeta(m.data);
      setContent(p.data.content || {});
      const ct = p.data.care_team || { physician_id: null, physician_ids: [], nurse_ids: [] };
      // back-compat: derive physician_ids from a legacy single physician_id
      if ((!ct.physician_ids || ct.physician_ids.length === 0) && ct.physician_id) ct.physician_ids = [ct.physician_id];
      setCareTeam({ ...ct, physician_ids: ct.physician_ids || [], nurse_ids: ct.nurse_ids || [] });
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const readOnly = !canEdit;
  const set = (path, val) => setContent((c) => {
    const next = structuredClone(c); let o = next;
    const ks = path.split("."); for (let i = 0; i < ks.length - 1; i++) { if (o[ks[i]] == null || typeof o[ks[i]] !== "object") o[ks[i]] = {}; o = o[ks[i]]; }
    o[ks[ks.length - 1]] = val; return next;
  });

  const payload = () => ({ content, care_team: careTeam });

  const saveDraft = async () => {
    setBusy("save");
    try { const r = await api.put(`/care-plans/${id}`, payload()); setPlan(r.data); toast.success("Draft saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const publish = async () => {
    setBusy("publish");
    try { const r = await api.post(`/care-plans/${id}/publish`, payload()); setPlan(r.data); setContent(r.data.content); toast.success(`Care plan published (v${r.data.version})`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (loading || !content || !meta) return <div className="p-8 text-sm text-[#6B7280]" data-testid="cp-loading">Loading care plan…</div>;

  // ── interventions & monitoring helpers (option + frequency rows) ──
  const setInterventionFreq = (type, freq) => {
    const arr = content.interventions || [];
    const exists = arr.find((x) => x.type === type);
    if (!freq) return set("interventions", arr.filter((x) => x.type !== type));
    set("interventions", exists ? arr.map((x) => x.type === type ? { ...x, frequency: freq } : x) : [...arr, { type, frequency: freq }]);
  };
  const interFreq = (type) => (content.interventions || []).find((x) => x.type === type)?.frequency || null;
  const setVitalFreq = (vital, freq) => {
    const arr = content.monitoring?.vitals || [];
    const exists = arr.find((x) => x.vital === vital);
    const nextArr = !freq ? arr.filter((x) => x.vital !== vital)
      : exists ? arr.map((x) => x.vital === vital ? { ...x, frequency: freq } : x) : [...arr, { vital, frequency: freq }];
    set("monitoring.vitals", nextArr);
  };
  const vitalFreq = (vital) => (content.monitoring?.vitals || []).find((x) => x.vital === vital)?.frequency || null;
  // Select all / clear all vitals in the monitoring plan
  const allVitalsSelected = meta.vitals.length > 0 && meta.vitals.every((v) => vitalFreq(v));
  const toggleAllVitals = () => {
    if (allVitalsSelected) set("monitoring.vitals", []);
    else set("monitoring.vitals", meta.vitals.map((v) => ({ vital: v, frequency: vitalFreq(v) || meta.vital_frequencies[0] })));
  };
  // Custom (physician-entered) interventions not in the predefined list
  const customInterventions = (content.interventions || []).filter((x) => !meta.intervention_options.includes(x.type));
  const addCustomIntervention = () => {
    const t = newInterv.trim();
    if (!t) return;
    if ((content.interventions || []).some((x) => x.type.toLowerCase() === t.toLowerCase())) { toast.error("This intervention is already added"); return; }
    set("interventions", [...(content.interventions || []), { type: t, frequency: meta.frequencies[0] }]);
    setNewInterv("");
  };

  // goals
  const addGoal = () => set("goals", [...(content.goals || []), { goal: meta.goal_options[0], target: "", target_date: "", status: "not_started" }]);
  const updGoal = (i, k, v) => set("goals", content.goals.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const delGoal = (i) => set("goals", content.goals.filter((_, idx) => idx !== i));
  // problems
  const addProblem = () => set("problems.problem_list", [...(content.problems?.problem_list || []), { problem: "", priority: "medium" }]);
  const updProblem = (i, k, v) => set("problems.problem_list", content.problems.problem_list.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const delProblem = (i) => set("problems.problem_list", content.problems.problem_list.filter((_, idx) => idx !== i));
  // discharge criteria
  const toggleCrit = (i) => set("discharge_criteria", content.discharge_criteria.map((c, idx) => idx === i ? { ...c, met: !c.met } : c));
  const addCrit = () => { if (!newCrit.trim()) return; set("discharge_criteria", [...(content.discharge_criteria || []), { criterion: newCrit.trim(), met: false }]); setNewCrit(""); };
  const delCrit = (i) => set("discharge_criteria", content.discharge_criteria.filter((_, idx) => idx !== i));

  const critMet = (content.discharge_criteria || []).filter((c) => c.met).length;
  const critTotal = (content.discharge_criteria || []).length;

  return (
    <div className="space-y-4 pb-24" data-testid="care-plan-editor">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="cp-back"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[#1F2937]">{plan.patient_name}</h1>
              <span className="font-mono text-sm text-[#0B7E8C]"><CopyableId value={plan.mrn} kind="MRN" /></span>
              <StatusBadge status={plan.status} />
              {plan.version > 0 && <span className="text-xs text-[#6B7280]">v{plan.version}</span>}
            </div>
            <div className="text-xs text-[#6B7280] mt-0.5">
              Care Plan{plan.episode_ref ? ` · ${plan.episode_ref}` : ""}{plan.published_by_name ? ` · published by ${plan.published_by_name}` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {plan.version > 0 && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/care-plans/${id}/versions`)} data-testid="cp-versions"><History className="w-4 h-4 mr-1.5" />Versions</Button>
          )}
          {canEdit && (
            <>
              <Button variant="outline" onClick={saveDraft} disabled={!!busy} data-testid="cp-save"><Save className="w-4 h-4 mr-1.5" />{busy === "save" ? "Saving…" : "Save draft"}</Button>
              <Button onClick={publish} disabled={!!busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cp-publish"><Send className="w-4 h-4 mr-1.5" />{busy === "publish" ? "Publishing…" : (plan.version > 0 ? "Publish update" : "Publish")}</Button>
            </>
          )}
        </div>
      </div>

      {readOnly && <div className="p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-sm text-[#1E40AF]" data-testid="cp-readonly-banner">View-only — this care plan is maintained by the responsible physician.</div>}

      {/* 1. Problems & Diagnoses */}
      <SectionCard title="Problems & Diagnoses" icon={Stethoscope} testId="cp-section-problems">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary diagnosis">
            <Input value={content.problems?.primary_diagnosis || ""} disabled={readOnly} onChange={(e) => set("problems.primary_diagnosis", e.target.value)} data-testid="cp-primary-dx" />
          </Field>
          <Field label="Comorbidities">
            <ChipMultiSelect options={toOpts(meta.comorbidity_options)} value={content.problems?.comorbidities || []} disabled={readOnly} onChange={(v) => set("problems.comorbidities", v)} testIdPrefix="cp-comorb" />
          </Field>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Problem list</label>
            {!readOnly && <Button variant="outline" size="sm" onClick={addProblem} data-testid="cp-add-problem"><Plus className="w-3.5 h-3.5 mr-1" />Add problem</Button>}
          </div>
          {(content.problems?.problem_list || []).length === 0 && <p className="text-sm text-[#94A3B8] italic">No problems added.</p>}
          {(content.problems?.problem_list || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <Input className="flex-1" placeholder="Problem" value={p.problem} disabled={readOnly} onChange={(e) => updProblem(i, "problem", e.target.value)} data-testid={`cp-problem-${i}`} />
              <div className="w-40"><Picker value={p.priority} options={toOpts(meta.priorities)} placeholder="Priority" disabled={readOnly} onChange={(v) => updProblem(i, "priority", v)} testId={`cp-problem-prio-${i}`} /></div>
              {!readOnly && <button onClick={() => delProblem(i)} className="text-[#EF4444] p-1.5" data-testid={`cp-del-problem-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* 2. Care Goals */}
      <SectionCard title="Care Goals" icon={Target} testId="cp-section-goals"
        actions={!readOnly && <Button variant="outline" size="sm" onClick={addGoal} data-testid="cp-add-goal"><Plus className="w-3.5 h-3.5 mr-1" />Add goal</Button>}>
        {(content.goals || []).length === 0 && <p className="text-sm text-[#94A3B8] italic">No goals yet. Add at least one before publishing.</p>}
        {(content.goals || []).map((g, i) => (
          <div key={i} className="grid md:grid-cols-12 gap-2 mb-3 items-end border-t border-[#F1F5F9] pt-3 first:border-t-0 first:pt-0">
            <div className="md:col-span-4"><Field label="Goal"><Picker value={g.goal} options={toOpts(meta.goal_options)} placeholder="Goal" disabled={readOnly} onChange={(v) => updGoal(i, "goal", v)} testId={`cp-goal-${i}`} /></Field></div>
            <div className="md:col-span-3"><Field label="Target"><Input value={g.target || ""} placeholder="e.g. CRP < 10" disabled={readOnly} onChange={(e) => updGoal(i, "target", e.target.value)} data-testid={`cp-goal-target-${i}`} /></Field></div>
            <div className="md:col-span-2"><Field label="Target date"><Input type="date" value={g.target_date || ""} disabled={readOnly} onChange={(e) => updGoal(i, "target_date", e.target.value)} data-testid={`cp-goal-date-${i}`} /></Field></div>
            <div className="md:col-span-2"><Field label="Status"><Picker value={g.status} options={toOpts(meta.goal_status)} placeholder="Status" disabled={readOnly} onChange={(v) => updGoal(i, "status", v)} testId={`cp-goal-status-${i}`} /></Field></div>
            <div className="md:col-span-1">{!readOnly && <button onClick={() => delGoal(i)} className="text-[#EF4444] p-2" data-testid={`cp-del-goal-${i}`}><Trash2 className="w-4 h-4" /></button>}</div>
          </div>
        ))}
      </SectionCard>

      {/* 3. Planned Interventions */}
      <SectionCard title="Planned Interventions" icon={Activity} desc="Select interventions and set a frequency for each." testId="cp-section-interventions">
        <div className="grid md:grid-cols-2 gap-2">
          {meta.intervention_options.map((opt) => {
            const f = interFreq(opt);
            return (
              <div key={opt} className={`flex items-center gap-2 p-2 rounded-lg border ${f ? "border-[#0B7E8C] bg-[#F0FBFC]" : "border-[#E5E7EB]"}`}>
                <Checkbox checked={!!f} disabled={readOnly} onCheckedChange={(c) => setInterventionFreq(opt, c ? meta.frequencies[0] : null)} data-testid={`cp-interv-${opt}`} />
                <span className="text-sm text-[#1F2937] flex-1">{opt}</span>
                {f && <div className="w-40"><Picker value={f} options={toOpts(meta.frequencies)} placeholder="Frequency" disabled={readOnly} onChange={(v) => setInterventionFreq(opt, v || meta.frequencies[0])} testId={`cp-interv-freq-${opt}`} /></div>}
              </div>
            );
          })}
          {/* Custom (physician-entered) interventions */}
          {customInterventions.map((x) => (
            <div key={x.type} className="flex items-center gap-2 p-2 rounded-lg border border-[#0B7E8C] bg-[#F0FBFC]" data-testid={`cp-interv-custom-${x.type}`}>
              <Checkbox checked disabled={readOnly} onCheckedChange={() => setInterventionFreq(x.type, null)} data-testid={`cp-interv-custom-check-${x.type}`} />
              <span className="text-sm text-[#1F2937] flex-1">{x.type} <span className="text-[10px] text-[#0B7E8C] uppercase tracking-wider">custom</span></span>
              <div className="w-40"><Picker value={x.frequency} options={toOpts(meta.frequencies)} placeholder="Frequency" disabled={readOnly} onChange={(v) => setInterventionFreq(x.type, v || meta.frequencies[0])} testId={`cp-interv-custom-freq-${x.type}`} /></div>
              {!readOnly && <button onClick={() => setInterventionFreq(x.type, null)} className="text-[#EF4444] p-1" data-testid={`cp-interv-custom-del-${x.type}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 mt-3">
            <Input placeholder="Add a custom intervention…" value={newInterv} onChange={(e) => setNewInterv(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomIntervention())} data-testid="cp-interv-custom-input" />
            <Button variant="outline" size="sm" onClick={addCustomIntervention} data-testid="cp-interv-custom-add"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
        )}
      </SectionCard>

      {/* 4. Monitoring */}
      <SectionCard title="Monitoring Plan" icon={HeartPulse} testId="cp-section-monitoring"
        actions={!readOnly && <Button variant="outline" size="sm" onClick={toggleAllVitals} data-testid="cp-vitals-select-all">{allVitalsSelected ? "Clear all" : "Select all"}</Button>}>
        <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#F0FBFC] border border-[#CFF0F3] w-fit">
          <Checkbox checked={!!content.monitoring?.rpm_enroll} disabled={readOnly} onCheckedChange={(c) => set("monitoring.rpm_enroll", !!c)} data-testid="cp-rpm-enroll" />
          <span className="text-sm text-[#1F2937]">Enroll in Remote Patient Monitoring (RPM)</span>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {meta.vitals.map((v) => {
            const f = vitalFreq(v);
            return (
              <div key={v} className={`flex items-center gap-2 p-2 rounded-lg border ${f ? "border-[#0B7E8C] bg-[#F0FBFC]" : "border-[#E5E7EB]"}`}>
                <Checkbox checked={!!f} disabled={readOnly} onCheckedChange={(c) => setVitalFreq(v, c ? meta.vital_frequencies[0] : null)} data-testid={`cp-vital-${v}`} />
                <span className="text-sm text-[#1F2937] flex-1">{v}</span>
                {f && <div className="w-40"><Picker value={f} options={toOpts(meta.vital_frequencies)} placeholder="Frequency" disabled={readOnly} onChange={(val) => setVitalFreq(v, val || meta.vital_frequencies[0])} testId={`cp-vital-freq-${v}`} /></div>}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* 5. Visit Plan */}
      <SectionCard title="Visit Plan" icon={CalendarClock} testId="cp-section-visit">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Physician visits / week"><Input type="number" min="0" value={content.visit_plan?.physician_visits_per_week ?? ""} disabled={readOnly} onChange={(e) => set("visit_plan.physician_visits_per_week", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="cp-visit-phys" /></Field>
          <Field label="Nurse visits / day"><Input type="number" min="0" value={content.visit_plan?.nurse_visits_per_day ?? ""} disabled={readOnly} onChange={(e) => set("visit_plan.nurse_visits_per_day", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="cp-visit-nurse" /></Field>
          <Field label="Teleconsults / week"><Input type="number" min="0" value={content.visit_plan?.teleconsults_per_week ?? ""} disabled={readOnly} onChange={(e) => set("visit_plan.teleconsults_per_week", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="cp-visit-tele" /></Field>
          <Field label="Expected discharge date"><Input type="date" value={content.visit_plan?.expected_discharge_date || ""} disabled={readOnly} onChange={(e) => set("visit_plan.expected_discharge_date", e.target.value)} data-testid="cp-visit-discharge" /></Field>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E5E7EB] space-y-3">
          <p className="text-xs text-[#6B7280]">On publish, visits are auto-scheduled from this cadence. Choose the forms the field team should fill for each visit type (optional).</p>
          <Field label="Forms for physician visits" full>
            {formOpts.length ? <ChipMultiSelect options={formOpts} value={content.visit_plan?.physician_form_ids || []} disabled={readOnly} onChange={(v) => set("visit_plan.physician_form_ids", v)} testIdPrefix="cp-visit-phys-form" /> : <span className="text-xs text-[#94A3B8]">No published forms available.</span>}
          </Field>
          <Field label="Forms for nurse visits" full>
            {formOpts.length ? <ChipMultiSelect options={formOpts} value={content.visit_plan?.nurse_form_ids || []} disabled={readOnly} onChange={(v) => set("visit_plan.nurse_form_ids", v)} testIdPrefix="cp-visit-nurse-form" /> : <span className="text-xs text-[#94A3B8]">No published forms available.</span>}
          </Field>
        </div>
      </SectionCard>

      {/* 6. Discharge Criteria */}
      <SectionCard title="Discharge Criteria" icon={ClipboardCheck} desc={`${critMet} of ${critTotal} criteria met`} testId="cp-section-discharge">
        <div className="space-y-1.5">
          {(content.discharge_criteria || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E7EB]">
              <Checkbox checked={!!c.met} disabled={readOnly} onCheckedChange={() => toggleCrit(i)} data-testid={`cp-crit-${i}`} />
              <span className={`text-sm flex-1 ${c.met ? "text-[#059669] line-through" : "text-[#1F2937]"}`}>{c.criterion}</span>
              {!readOnly && <button onClick={() => delCrit(i)} className="text-[#EF4444] p-1" data-testid={`cp-del-crit-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 mt-3">
            <Input placeholder="Add custom criterion…" value={newCrit} onChange={(e) => setNewCrit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCrit()} data-testid="cp-new-crit" />
            <Button variant="outline" size="sm" onClick={addCrit} data-testid="cp-add-crit"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
        )}
      </SectionCard>

      {/* 7. Escalation */}
      <SectionCard title="Escalation Plan" icon={Siren} testId="cp-section-escalation">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Escalation triggers">
            <ChipMultiSelect options={toOpts(meta.escalation_triggers)} value={content.escalation?.triggers || []} disabled={readOnly} onChange={(v) => set("escalation.triggers", v)} testIdPrefix="cp-esc-trigger" />
          </Field>
          <Field label="Escalation contact">
            <Picker value={content.escalation?.contact} options={toOpts(meta.escalation_contacts)} placeholder="Contact" disabled={readOnly} onChange={(v) => set("escalation.contact", v)} testId="cp-esc-contact" />
          </Field>
        </div>
      </SectionCard>

      {/* 8. Logistics */}
      <SectionCard title="Activity, Diet & Equipment" icon={Utensils} testId="cp-section-logistics">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Activity level"><Picker value={content.logistics?.activity_level} options={toOpts(meta.activity_levels)} placeholder="Activity level" disabled={readOnly} onChange={(v) => set("logistics.activity_level", v)} testId="cp-activity" /></Field>
          <Field label="Diet"><Picker value={content.logistics?.diet} options={toOpts(meta.diet_options)} placeholder="Diet" disabled={readOnly} onChange={(v) => set("logistics.diet", v)} testId="cp-diet" /></Field>
          <Field label="Equipment needed" full><ChipMultiSelect options={toOpts(meta.equipment_options)} value={content.logistics?.equipment || []} disabled={readOnly} onChange={(v) => set("logistics.equipment", v)} testIdPrefix="cp-equip" /></Field>
        </div>
      </SectionCard>

      {/* 9. Care Team */}
      <SectionCard title="Care Team" icon={Users2} desc="The published plan is shared with these clinicians + anyone assigned to the patient." testId="cp-section-team">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Responsible physicians" hint="First selected is the primary/responsible physician.">
            <ChipMultiSelect options={meta.physicians.map((p) => ({ value: p.id, label: `${p.first_name} ${p.last_name}` }))} value={careTeam.physician_ids || []} disabled={readOnly} onChange={(v) => setCareTeam((t) => ({ ...t, physician_ids: v, physician_id: v[0] || null }))} testIdPrefix="cp-team-physician" />
          </Field>
          <Field label="Nurses">
            <ChipMultiSelect options={meta.nurses.map((n) => ({ value: n.id, label: `${n.first_name} ${n.last_name}` }))} value={careTeam.nurse_ids || []} disabled={readOnly} onChange={(v) => setCareTeam((t) => ({ ...t, nurse_ids: v }))} testIdPrefix="cp-team-nurse" />
          </Field>
        </div>
      </SectionCard>

      {/* 10. Notes */}
      <SectionCard title="Additional Notes" icon={FileText} testId="cp-section-notes">
        <Textarea rows={3} placeholder="Optional short clinical note…" value={content.notes || ""} disabled={readOnly} onChange={(e) => set("notes", e.target.value)} data-testid="cp-notes" />
      </SectionCard>
    </div>
  );
}
